import React, { useState } from "react";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { Row, Col, Table, Accordion, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { useDownloadPdf } from "../../../../api/service-worksheet-api";
import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import { createErrorFeedback } from "../../../../context/ui-feedback/action";

export const SalesOrderDetails = ({
  swId,
  onSiteWorksheet,
  salesOrder,
  status,
  isMobilSync,
  created,
  createdDate,
  modify,
  modifyDate,
  pdf,
  shortPdf,
  email,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(null);

  const { uiFeedbackDispatch } = useUiFeedback();

  const downloadPdf = useDownloadPdf();

  const DataRow = ({ label, value, action }) => (
    <Row>
      <Col sm="6" md="4" lg="3">
        {label ? `${label}:` : null}
      </Col>
      <Col sm="6" md="8" lg="9">
        {action ? (
          <Button variant="link" onClick={action} className="p-0">
            {value}
          </Button>
        ) : (
          <b>{value}</b>
        )}
      </Col>
    </Row>
  );

  const addString = (text, value, separator) => {
    if (!value || value.length === 0) {
      return text;
    }
    return text + (text.length > 0 ? separator : "") + value;
  };

  const createShipData = (ship) => {
    let response = ship.name;
    let address = addString("", ship.postCode, "");
    address = addString(address, ship.city, ", ");
    address = addString(address, ship.addressLine1, " ");
    address = addString(address, ship.addressLine2, " ");
    return response + (address.length > 0 ? ` (${address})` : "");
  };

  const createContactData = (contact) => {
    let response = addString("", contact.shipToContact, " ");
    let contactText = addString("", contact.phoneNumber, ", ");
    contactText = addString(contactText, contact.email, ", ");
    return response + (contactText.length > 0 ? ` (${contactText})` : "");
  };

  const TableBody = ({ items }) => {
    if (items.length === 0) {
      return null;
    }

    return items.map((item, index) => (
      <tr key={item.id}>
        <td className="col-rownumber">{index + 1}.</td>
        <td className="col-item-number">{item.lineObjectNumber}</td>
        <td className="col-item-description">
          {item.description} {item.description2}
        </td>
        <td className="col-item-quantity">
          {item.quantity} {item.unitOfMeasureCode}
        </td>
      </tr>
    ));
  };

  const download = async (serviceWorksheetId, pdfName, shortVersion) => {
    try {
      await downloadPdf(serviceWorksheetId, pdfName, shortVersion, (data) => {
        if (data instanceof Error) {
          throw data;
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadpdf.title",
          }),
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadpdf.description",
          }),
        );
      }
    }
  };

  const ItemsAccordion = ({ items }) => {
    return (
      <Accordion
        defaultActiveKey={activeTab}
        onSelect={(ev) => {
          setActiveTab(ev);
        }}
      >
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <FormattedMessage id="page.editserviceworksheet.accordion.items" />
          </Accordion.Header>
          <Accordion.Body>
            <div className="template-table-component table-responsive">
              <Table striped bordered>
                <thead>
                  <tr>
                    <th className="col-rownumber">#</th>
                    <th className="col-item-number">
                      <FormattedMessage id="data.serviceworksheet.form.salesorder.item.number" />
                    </th>
                    <th className="col-item-description">
                      <FormattedMessage id="data.serviceworksheet.form.salesorder.item.description" />
                    </th>
                    <th className="col-item-quantity">
                      <FormattedMessage id="data.serviceworksheet.form.salesorder.item.quantity" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <TableBody items={items} />
                </tbody>
              </Table>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  return (
    <>
      <div className="mb-4">
        <DataRow
          label={intl.formatMessage({
            id: "data.serviceworksheet.form.salesorder.number.label",
          })}
          value={
            onSiteWorksheet
              ? intl.formatMessage({
                  id: "page.serviceworksheets.label.onsiteservice",
                })
              : salesOrder.number
          }
        />
        <DataRow
          label={intl.formatMessage({
            id: "data.serviceworksheet.form.salesorder.customer",
          })}
          value={salesOrder.customer.name}
        />
        {onSiteWorksheet ? null : (
          <DataRow
            label={intl.formatMessage({
              id: "data.serviceworksheet.form.salesorder.ship",
            })}
            value={createShipData(salesOrder.ship)}
          />
        )}
        {onSiteWorksheet ? null : (
          <DataRow
            label={intl.formatMessage({
              id: "data.serviceworksheet.form.salesorder.contact",
            })}
            value={createContactData(salesOrder.contact)}
          />
        )}
        <hr />
        <DataRow
          label={intl.formatMessage({
            id: "data.serviceworksheet.form.created",
          })}
          value={`${createdDate} (${created.name})`}
        />
        <DataRow
          label={intl.formatMessage({
            id: "data.serviceworksheet.form.modify",
          })}
          value={`${modifyDate} (${modify.name})`}
        />
        <DataRow
          label={intl.formatMessage({
            id: "data.serviceworksheet.form.status.label",
          })}
          value={intl.formatMessage({
            id: `data.serviceworksheet.form.status.value.${status}`,
          })}
        />
        {status === "ALLOCATED" && isMobilSync ? (
          <DataRow
            label={null}
            value={`(${intl.formatMessage({ id: "data.serviceworksheet.data.status.sync" })})`}
          />
        ) : null}
        {pdf ? (
          <div>
            <DataRow
              label={intl.formatMessage({
                id: "data.serviceworksheet.form.pdf",
              })}
              value={pdf}
              action={() => {
                download(swId, pdf, false);
              }}
            />
            {shortPdf ? (
              <DataRow
                value={shortPdf}
                action={() => {
                  download(swId, shortPdf, true);
                }}
              />
            ) : null}
          </div>
        ) : null}
        {email ? (
          <DataRow
            label={intl.formatMessage({
              id: "data.serviceworksheet.form.emailstatus.label",
            })}
            value={intl.formatMessage({
              id: `data.serviceworksheet.form.emailstatus.value.${email}`,
            })}
          />
        ) : null}
        <hr />
      </div>
      {onSiteWorksheet ? null : <ItemsAccordion items={salesOrder.items} />}
    </>
  );
};

SalesOrderDetails.propTypes = {
  swId: PropTypes.number.isRequired,
  onSiteWorksheet: PropTypes.bool.isRequired,
  salesOrder: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  isMobilSync: PropTypes.bool.isRequired,
  created: PropTypes.object.isRequired,
  createdDate: PropTypes.string.isRequired,
  modify: PropTypes.object.isRequired,
  modifyDate: PropTypes.string.isRequired,
  pdf: PropTypes.string,
  shortPdf: PropTypes.string,
  email: PropTypes.string,
};
